import { ReferenceCreationContext } from '../../reference'
import { remapPuppyAssemblyNodeReferences } from './remapPuppyAssemblyNodeReferences'
import {
  PuppyDatumExtensionSchemaVersion,
  type PuppyDatumAxisFeatureRecord,
  type PuppyDatumAxisRecord,
  type PuppyDatumExtension,
  type PuppyDatumExtensionInput,
  type PuppyDatumPlaneRecord,
  type PuppyDatumPointFeatureRecord,
  type PuppyDatumPointRecord,
} from './types'

//------ 离线包基准扩展 开始------
/**
 * assembly.json.extensions 中保存基准数据的字段名。
 */
export const PUPPY_DATUM_EXTENSION_KEY = 'datums'

/**
 * 基准扩展列表字段名。
 */
enum PuppyDatumExtensionListKey {
  /** 用户创建的正式基准点列表。 */
  DatumPointList = 'datumPointList',
  /** 用户创建的基准点特征定义列表。 */
  DatumPointFeatureList = 'datumPointFeatureList',
  /** 用户创建的正式基准轴列表。 */
  DatumAxisList = 'datumAxisList',
  /** 用户创建的基准轴特征定义列表。 */
  DatumAxisFeatureList = 'datumAxisFeatureList',
  /** 用户创建的正式基准面列表。 */
  DatumPlaneList = 'datumPlaneList',
}

/**
 * 有效基准创建上下文集合。
 */
const REFERENCE_CREATION_CONTEXT_VALUES = new Set<string>(Object.values(ReferenceCreationContext))

/**
 * 判断未知值是否为普通记录对象。
 *
 * @param value 未知值。
 * @returns 是否为可读记录对象。
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

/**
 * 判断未知值是否为可序列化 plain data。
 *
 * @param value 未知值。
 * @returns 是否为 JSON 可持久化数据。
 */
function isSerializablePlainData(value: unknown): boolean {
  if (
    value === null
    || typeof value === 'string'
    || typeof value === 'boolean'
  ) {
    return true
  }
  if (typeof value === 'number') {
    return Number.isFinite(value)
  }
  if (Array.isArray(value)) {
    return value.every(isSerializablePlainData)
  }
  if (!isRecord(value)) {
    return false
  }
  return Object.values(value).every(isSerializablePlainData)
}

/**
 * 深拷贝可序列化数据。
 *
 * @param value 源数据。
 * @returns 深拷贝后的数据。
 */
function cloneSerializable<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

/**
 * 断言字段是非空字符串。
 *
 * @param value 字段值。
 * @param path 字段路径。
 */
function assertNonEmptyString(value: unknown, path: string): asserts value is string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new Error(`PuppyCAD 离线包基准扩展字段必须是非空字符串: ${path}`)
  }
}

/**
 * 断言字段是有限数字。
 *
 * @param value 字段值。
 * @param path 字段路径。
 */
function assertFiniteNumber(value: unknown, path: string): asserts value is number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`PuppyCAD 离线包基准扩展字段必须是有限数字: ${path}`)
  }
}

/**
 * 断言字段是布尔值。
 *
 * @param value 字段值。
 * @param path 字段路径。
 */
function assertBoolean(value: unknown, path: string): asserts value is boolean {
  if (typeof value !== 'boolean') {
    throw new Error(`PuppyCAD 离线包基准扩展字段必须是布尔值: ${path}`)
  }
}

/**
 * 断言字段是基准创建上下文。
 *
 * @param value 字段值。
 * @param path 字段路径。
 */
function assertReferenceCreationContext(value: unknown, path: string): asserts value is ReferenceCreationContext {
  if (typeof value !== 'string' || !REFERENCE_CREATION_CONTEXT_VALUES.has(value)) {
    throw new Error(`PuppyCAD 离线包基准扩展字段 creationContext 无效: ${path}`)
  }
  if (value === ReferenceCreationContext.DefaultBuiltIn) {
    throw new Error(`PuppyCAD 离线包基准扩展不允许恢复默认内置对象: ${path}`)
  }
}

/**
 * 断言字段是三维向量元组。
 *
 * @param value 字段值。
 * @param path 字段路径。
 */
function assertVector3Tuple(value: unknown, path: string): asserts value is readonly [number, number, number] {
  if (
    !Array.isArray(value)
    || value.length !== 3
    || value.some((item) => typeof item !== 'number' || !Number.isFinite(item))
  ) {
    throw new Error(`PuppyCAD 离线包基准扩展字段必须是三维有限数字向量: ${path}`)
  }
}

/**
 * 断言字段是正有限数字。
 *
 * @param value 字段值。
 * @param path 字段路径。
 */
function assertPositiveFiniteNumber(value: unknown, path: string): asserts value is number {
  assertFiniteNumber(value, path)
  if (value <= 0) {
    throw new Error(`PuppyCAD 离线包基准扩展字段必须是正有限数字: ${path}`)
  }
}

/**
 * 断言字段是可序列化对象。
 *
 * @param value 字段值。
 * @param path 字段路径。
 */
function assertSerializableObject(value: unknown, path: string): asserts value is Record<string, unknown> {
  if (!isRecord(value) || !isSerializablePlainData(value)) {
    throw new Error(`PuppyCAD 离线包基准扩展字段必须是可序列化对象: ${path}`)
  }
}

/**
 * 读取基准扩展列表。
 *
 * @param record 基准扩展记录。
 * @param key 列表字段名。
 * @param assertItem 单项校验函数。
 * @returns 已克隆的列表。
 */
function readDatumList<TRecord>(
  record: Record<string, unknown>,
  key: PuppyDatumExtensionListKey,
  assertItem: (value: unknown, path: string) => void,
): TRecord[] {
  const value = record[key]
  if (!Array.isArray(value)) {
    throw new Error(`PuppyCAD 离线包基准扩展字段必须是数组: ${key}`)
  }

  /**
   * 解析阶段立即逐项校验，避免导入挂载时才暴露半恢复状态。
   */
  value.forEach((item, index) => {
    assertItem(item, `${key}[${index}]`)
  })
  return cloneSerializable(value) as TRecord[]
}

/**
 * 断言单条基准点记录合法。
 *
 * @param value 字段值。
 * @param path 字段路径。
 */
function assertDatumPointRecord(value: unknown, path: string): void {
  if (!isRecord(value)) {
    throw new Error(`PuppyCAD 离线包基准点记录必须是对象: ${path}`)
  }
  assertFiniteNumber(value.pointId, `${path}.pointId`)
  assertNonEmptyString(value.name, `${path}.name`)
  assertReferenceCreationContext(value.creationContext, `${path}.creationContext`)
  assertVector3Tuple(value.position, `${path}.position`)
  assertBoolean(value.visible, `${path}.visible`)
  assertBoolean(value.isOrigin, `${path}.isOrigin`)
  if (value.isOrigin === true) {
    throw new Error(`PuppyCAD 离线包基准点列表不允许恢复默认原点: ${path}`)
  }
}

/**
 * 断言单条基准点特征记录合法。
 *
 * @param value 字段值。
 * @param path 字段路径。
 */
function assertDatumPointFeatureRecord(value: unknown, path: string): void {
  assertSerializableObject(value, path)
  assertNonEmptyString(value.featureId, `${path}.featureId`)
  assertReferenceCreationContext(value.creationContext, `${path}.creationContext`)
  assertSerializableObject(value.definition, `${path}.definition`)
  if (!Array.isArray(value.datumPointIds) || value.datumPointIds.some((item) => typeof item !== 'number' || !Number.isFinite(item))) {
    throw new Error(`PuppyCAD 离线包基准点特征 datumPointIds 必须是有限数字数组: ${path}.datumPointIds`)
  }
}

/**
 * 断言单条基准轴记录合法。
 *
 * @param value 字段值。
 * @param path 字段路径。
 */
function assertDatumAxisRecord(value: unknown, path: string): void {
  if (!isRecord(value)) {
    throw new Error(`PuppyCAD 离线包基准轴记录必须是对象: ${path}`)
  }
  assertFiniteNumber(value.axisId, `${path}.axisId`)
  assertNonEmptyString(value.name, `${path}.name`)
  assertReferenceCreationContext(value.creationContext, `${path}.creationContext`)
  assertVector3Tuple(value.originWorld, `${path}.originWorld`)
  assertVector3Tuple(value.direction, `${path}.direction`)
  assertVector3Tuple(value.startWorld, `${path}.startWorld`)
  assertVector3Tuple(value.endWorld, `${path}.endWorld`)
  assertFiniteNumber(value.color, `${path}.color`)
  assertBoolean(value.visible, `${path}.visible`)
  assertFiniteNumber(value.opacity, `${path}.opacity`)
}

/**
 * 断言单条基准轴特征记录合法。
 *
 * @param value 字段值。
 * @param path 字段路径。
 */
function assertDatumAxisFeatureRecord(value: unknown, path: string): void {
  assertSerializableObject(value, path)
  assertNonEmptyString(value.featureId, `${path}.featureId`)
  assertReferenceCreationContext(value.creationContext, `${path}.creationContext`)
  assertSerializableObject(value.definition, `${path}.definition`)
  assertBoolean(value.hidden, `${path}.hidden`)
}

/**
 * 断言单条基准面记录合法。
 *
 * @param value 字段值。
 * @param path 字段路径。
 */
function assertDatumPlaneRecord(value: unknown, path: string): void {
  if (!isRecord(value)) {
    throw new Error(`PuppyCAD 离线包基准面记录必须是对象: ${path}`)
  }
  assertFiniteNumber(value.planeId, `${path}.planeId`)
  assertNonEmptyString(value.name, `${path}.name`)
  assertReferenceCreationContext(value.creationContext, `${path}.creationContext`)
  assertVector3Tuple(value.originWorld, `${path}.originWorld`)
  assertVector3Tuple(value.normal, `${path}.normal`)
  assertVector3Tuple(value.xAxis, `${path}.xAxis`)
  assertVector3Tuple(value.yAxis, `${path}.yAxis`)
  assertPositiveFiniteNumber(value.width, `${path}.width`)
  assertPositiveFiniteNumber(value.height, `${path}.height`)
  assertFiniteNumber(value.color, `${path}.color`)
  assertBoolean(value.visible, `${path}.visible`)
  assertFiniteNumber(value.opacity, `${path}.opacity`)
  if (value.calculationSource !== undefined && value.calculationSource !== null) {
    assertSerializableObject(value.calculationSource, `${path}.calculationSource`)
  }
}

/**
 * 断言列表内稳定数字 ID 不重复。
 *
 * @param records 待检查记录。
 * @param readId 读取稳定 ID 的函数。
 * @param label 错误上下文标签。
 */
function assertUniqueNumberIds<TRecord>(
  records: readonly TRecord[],
  readId: (record: TRecord) => number,
  label: string,
): void {
  const seen = new Set<number>()
  records.forEach((record) => {
    const id = readId(record)
    if (seen.has(id)) {
      throw new Error(`PuppyCAD 离线包基准扩展存在重复 ${label}: ${id}`)
    }
    seen.add(id)
  })
}

/**
 * 根据输入创建离线包基准扩展。
 *
 * @param input 基准扩展输入。
 * @returns 有用户基准数据时返回扩展；没有数据时返回 `undefined`。
 */
export function createPuppyDatumExtension(
  input: PuppyDatumExtensionInput | null | undefined,
): PuppyDatumExtension | undefined {
  if (!input) {
    return undefined
  }

  const datumExtension: PuppyDatumExtension = {
    schemaVersion: PuppyDatumExtensionSchemaVersion.V1,
    datumPointList: cloneSerializable([...(input.datumPointList ?? [])]),
    datumPointFeatureList: cloneSerializable([...(input.datumPointFeatureList ?? [])]),
    datumAxisList: cloneSerializable([...(input.datumAxisList ?? [])]),
    datumAxisFeatureList: cloneSerializable([...(input.datumAxisFeatureList ?? [])]),
    datumPlaneList: cloneSerializable([...(input.datumPlaneList ?? [])]),
  }

  /**
   * 无用户基准对象时不写入扩展字段，让旧导入链路保持简洁。
   */
  const hasDatumData = datumExtension.datumPointList.length > 0
    || datumExtension.datumPointFeatureList.length > 0
    || datumExtension.datumAxisList.length > 0
    || datumExtension.datumAxisFeatureList.length > 0
    || datumExtension.datumPlaneList.length > 0
  return hasDatumData ? parsePuppyDatumExtension(datumExtension) : undefined
}

/**
 * 解析 `.puppy` 基准扩展。
 *
 * @param value 未知 JSON 值。
 * @returns 已校验的基准扩展。
 * @throws 当输入不是对象或扩展版本不受支持时。
 */
export function parsePuppyDatumExtension(value: unknown): PuppyDatumExtension {
  if (!isRecord(value)) {
    throw new Error('PuppyCAD 离线包基准扩展必须是对象')
  }
  if (value.schemaVersion !== PuppyDatumExtensionSchemaVersion.V1) {
    throw new Error(`不支持的 PuppyCAD 离线包基准扩展版本: ${String(value.schemaVersion)}`)
  }

  const datumExtension: PuppyDatumExtension = {
    schemaVersion: PuppyDatumExtensionSchemaVersion.V1,
    datumPointList: readDatumList<PuppyDatumPointRecord>(value, PuppyDatumExtensionListKey.DatumPointList, assertDatumPointRecord),
    datumPointFeatureList: readDatumList<PuppyDatumPointFeatureRecord>(value, PuppyDatumExtensionListKey.DatumPointFeatureList, assertDatumPointFeatureRecord),
    datumAxisList: readDatumList<PuppyDatumAxisRecord>(value, PuppyDatumExtensionListKey.DatumAxisList, assertDatumAxisRecord),
    datumAxisFeatureList: readDatumList<PuppyDatumAxisFeatureRecord>(value, PuppyDatumExtensionListKey.DatumAxisFeatureList, assertDatumAxisFeatureRecord),
    datumPlaneList: readDatumList<PuppyDatumPlaneRecord>(value, PuppyDatumExtensionListKey.DatumPlaneList, assertDatumPlaneRecord),
  }

  assertUniqueNumberIds(datumExtension.datumPointList, (record) => record.pointId, 'datumPointList.pointId')
  assertUniqueNumberIds(datumExtension.datumAxisList, (record) => record.axisId, 'datumAxisList.axisId')
  assertUniqueNumberIds(datumExtension.datumPlaneList, (record) => record.planeId, 'datumPlaneList.planeId')
  return datumExtension
}

/**
 * 从 assembly.json extensions 中读取基准扩展。
 *
 * @param extensions assembly.json.extensions。
 * @returns 存在时返回基准扩展；不存在时返回 `undefined`。
 */
export function readPuppyDatumExtensionFromAssemblyExtensions(
  extensions: Readonly<Record<string, unknown>> | undefined,
): PuppyDatumExtension | undefined {
  const value = extensions?.[PUPPY_DATUM_EXTENSION_KEY]
  return value === undefined ? undefined : parsePuppyDatumExtension(value)
}

/**
 * 把基准扩展写入 assembly.json extensions。
 *
 * @param extensions 原始扩展字段。
 * @param datumExtension 基准扩展。
 * @returns 写入后的扩展字段；没有基准数据时返回原扩展副本。
 */
export function writePuppyDatumExtensionToAssemblyExtensions(
  extensions: Readonly<Record<string, unknown>> | undefined,
  datumExtension: PuppyDatumExtension | undefined,
): Record<string, unknown> | undefined {
  const nextExtensions = extensions ? { ...extensions } : {}
  if (datumExtension) {
    nextExtensions[PUPPY_DATUM_EXTENSION_KEY] = datumExtension
  } else {
    delete nextExtensions[PUPPY_DATUM_EXTENSION_KEY]
  }
  return Object.keys(nextExtensions).length > 0 ? nextExtensions : undefined
}

/**
 * 重映射基准扩展中的装配节点引用。
 *
 * @param input.datumExtension 原始基准扩展。
 * @param input.aliasByNodeId 旧节点 ID 到新节点 ID 的确定性映射。
 * @returns 使用目标节点 ID 的基准扩展。
 */
export function remapPuppyDatumExtensionNodeReferences(input: {
  readonly datumExtension: PuppyDatumExtension
  readonly aliasByNodeId: Readonly<Record<string, string>>
}): PuppyDatumExtension {
  return remapPuppyAssemblyNodeReferences({
    value: input.datumExtension,
    aliasByNodeId: input.aliasByNodeId,
  })
}
//------ 离线包基准扩展 结束------
